import {useMemo, useState, useTransition} from 'react';
import {Link} from 'react-router-dom';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  Pencil,
  Search,
  Users
} from 'lucide-react';
import {
  Alert,
  AlertTitle,
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Spinner,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import {useQcAnalysts, useUpdateQcRequiredVerticals, useBulkApproveQcReview} from '@/api/hooks';
import {getApiErrorMessage} from '@/api/client';
import {type QcAnalystSummary} from '@/api/qcReview';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {cn} from '@/lib/utils';
import {QC_GATE_TYPES, gateKey, parseQcGate, serializeQcGate, type QcGateType} from '@/lib/qcGate';
import {TablePanel} from '@/components/ui/table-panel';
import {showToast} from '@/lib/toast';
import {useDebounce} from '@/lib/useDebounce';

function formatPercent(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase() || '?';
}

export type QcAnalystStatusFilter = 'all' | 'training' | 'cleared';

const STATUS_FILTERS: Array<{value: QcAnalystStatusFilter; label: string}> = [
  {value: 'all', label: 'All'},
  {value: 'training', label: 'In training'},
  {value: 'cleared', label: 'Cleared'}
];

function isStatusFilter(value: unknown): value is QcAnalystStatusFilter {
  return value === 'all' || value === 'training' || value === 'cleared';
}

type SortKey = 'name' | 'accuracy' | 'pending';

function SortableHeader({
  label,
  sortKey,
  activeKey,
  direction,
  onSort,
  align = 'left'
}: {
  readonly label: string;
  readonly sortKey: SortKey;
  readonly activeKey: SortKey;
  readonly direction: 'asc' | 'desc';
  readonly onSort: (key: SortKey) => void;
  readonly align?: 'left' | 'right';
}) {
  const isActive = activeKey === sortKey;
  const Icon = isActive ? (direction === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <TableHead className={align === 'right' ? 'text-right' : undefined}>
      <Button
        type="button"
        variant="ghost"
        size="xs"
        className={cn('-mx-2', isActive ? 'text-foreground' : 'text-muted-foreground')}
        onClick={() => {
          onSort(sortKey);
        }}>
        {label}
        <Icon />
      </Button>
    </TableHead>
  );
}

const MAX_CHIPS = 3;

/**
 Verticals this analyst is gated on (QC applies) for one submission type. Short lists show the
 names; a long list reads as whichever is shorter - "All except X, Y" or "N of M" - so many verticals
 never turns the cell into a wall of badges. The full list is always in the tooltip.
 */
function QcTypeGateCell({
  qcRequiredVerticals,
  verticals,
  type
}: {
  readonly qcRequiredVerticals: string[] | null;
  readonly verticals: string[];
  readonly type: QcGateType;
}) {
  if (qcRequiredVerticals === null) {
    return <Badge variant="outline">All verticals</Badge>;
  }

  const gatedKeys = parseQcGate(qcRequiredVerticals);
  const gated = verticals.filter((v) => gatedKeys.has(gateKey(v, type)));
  const released = verticals.filter((v) => !gatedKeys.has(gateKey(v, type)));
  if (gated.length === 0) {
    return <Badge variant="secondary">Cleared</Badge>;
  }

  if (released.length === 0) {
    return <Badge variant="outline">All verticals</Badge>;
  }

  const tooltip = `QC on: ${gated.join(', ')}. Skips QC: ${released.join(', ')}.`;
  if (gated.length <= MAX_CHIPS) {
    return (
      <div className="flex flex-wrap gap-1" title={tooltip}>
        {gated.map((v) => (
          <Badge key={v} variant="outline">
            {v}
          </Badge>
        ))}
      </div>
    );
  }

  // A Tooltip rather than a native title, so the full list is reachable from the keyboard and
  // announced, not only shown to a hovering pointer.
  return (
    <Tooltip>
      <TooltipTrigger render={<Badge variant="outline" />}>
        {released.length <= MAX_CHIPS
          ? `All except ${released.join(', ')}`
          : `${gated.length} of ${verticals.length} verticals`}
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{tooltip}</TooltipContent>
    </Tooltip>
  );
}

/**
 Editor for one analyst's per-vertical QC gate. "All verticals"/"None" are the same all-or-nothing
 shortcut UpdateRequiresQcReview exposes; the toggle grid is anything in between.
 */
function QcVerticalsModal({
  analyst,
  verticals,
  onSave,
  onClose
}: {
  readonly analyst: QcAnalystSummary;
  readonly verticals: string[];
  readonly onSave: (qcRequiredVerticals: string[] | null) => Promise<void>;
  readonly onClose: () => void;
}) {
  // Expanded "Vertical:Type" keys; null = every vertical and type (see lib/qcGate).
  const [selected, setSelected] = useState<Set<string> | null>(
    analyst.qc_required_verticals === null ? null : parseQcGate(analyst.qc_required_verticals)
  );
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  function submit() {
    setError(null);
    startTransition(async () => {
      try {
        await onSave(selected === null ? null : serializeQcGate(selected));
        onClose();
      } catch (error_) {
        setError(getApiErrorMessage(error_, 'Failed to update QC verticals.'));
      }
    });
  }

  const mode = selected === null ? 'all' : selected.size === 0 ? 'none' : null;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>QC verticals — {analyst.display_name}</DialogTitle>
          <DialogDescription>
            Verticals and submission types this analyst is gated on. A risky submission in a
            selected vertical and type is held for QC review; everything else submits directly.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <ToggleGroup
            variant="outline"
            spacing={0}
            size="sm"
            aria-label="Gate scope"
            value={mode ? [mode] : []}
            onValueChange={(next) => {
              const value: unknown = next[0];
              if (value === 'all') {
                setSelected(null);
              } else if (value === 'none') {
                setSelected(new Set());
              }
            }}>
            <ToggleGroupItem value="all">All verticals</ToggleGroupItem>
            <ToggleGroupItem value="none">None</ToggleGroupItem>
          </ToggleGroup>

          <div className="flex flex-col gap-1.5">
            {verticals.map((v) => (
              <div key={v} className="flex items-center justify-between gap-3">
                <span className="truncate text-xs">{v}</span>
                <ToggleGroup
                  multiple
                  variant="outline"
                  size="sm"
                  aria-label={`${v} submission types requiring QC`}
                  value={QC_GATE_TYPES.filter(
                    (t) => selected === null || selected.has(gateKey(v, t))
                  )}
                  onValueChange={(next) => {
                    // Toggling any cell materializes "all" into explicit keys first.
                    const keys =
                      selected ??
                      new Set(
                        verticals.flatMap((vertical) =>
                          QC_GATE_TYPES.map((t) => gateKey(vertical, t))
                        )
                      );
                    const updated = new Set(keys);
                    for (const t of QC_GATE_TYPES) {
                      if (next.includes(t)) {
                        updated.add(gateKey(v, t));
                      } else {
                        updated.delete(gateKey(v, t));
                      }
                    }

                    setSelected(updated);
                  }}>
                  {QC_GATE_TYPES.map((t) => (
                    <ToggleGroupItem key={t} value={t}>
                      {t}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </div>
            ))}
          </div>

          {error ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertTitle>{error}</AlertTitle>
            </Alert>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={pending} onClick={submit}>
            {pending ? <Spinner /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// The search box and status toggle. Rendered by the page in its filter bar, above the table, so
// both QC Review tabs keep every control in the same white strip.
export function QcAnalystFilters({
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange
}: {
  readonly search: string;
  readonly onSearchChange: (search: string) => void;
  readonly statusFilter: QcAnalystStatusFilter;
  readonly onStatusFilterChange: (statusFilter: QcAnalystStatusFilter) => void;
}) {
  // A fragment, so both controls join the page's filter row rather than wrapping as one block.
  return (
    <>
      <InputGroup className="w-56 max-w-full">
        <InputGroupAddon>
          <Search />
        </InputGroupAddon>
        <InputGroupInput
          value={search}
          placeholder="Search analysts…"
          aria-label="Search analysts"
          onChange={(event) => {
            onSearchChange(event.target.value);
          }}
        />
      </InputGroup>

      <ToggleGroup
        variant="outline"
        spacing={0}
        size="sm"
        aria-label="Analyst status"
        value={[statusFilter]}
        onValueChange={(next) => {
          const value: unknown = next[0];
          if (isStatusFilter(value)) {
            onStatusFilterChange(value);
          }
        }}>
        {STATUS_FILTERS.map((filter) => (
          <ToggleGroupItem key={filter.value} value={filter.value}>
            {filter.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </>
  );
}

/**
 Analyst Management tab: every analyst, their blended accuracy (Weekly QA's sampled reviews plus this
 analyst's own QC approve/reject outcomes, computed server-side in one figure), their current
 QC-pending backlog, and one-click controls a lead needs day to day - edit their per-vertical QC gate,
 or clear a whole backlog in one call.
 */
const COLUMN_COUNT = 6;

export function QcAnalystTable({
  verticals,
  search,
  statusFilter
}: {
  readonly verticals: string[];
  readonly search: string;
  readonly statusFilter: QcAnalystStatusFilter;
}) {
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [editingAnalyst, setEditingAnalyst] = useState<QcAnalystSummary | null>(null);
  // Debounced so typing a name sends one request when the analyst pauses, not one per keystroke.
  const debouncedSearch = useDebounce(search, 300);
  const {data: analysts = [], isLoading} = useQcAnalysts(debouncedSearch || undefined);
  const updateVerticals = useUpdateQcRequiredVerticals();
  const bulkApprove = useBulkApproveQcReview();

  function handleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((previous) => (previous === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Accuracy/pending read more naturally starting high-to-low (worst backlog, lowest accuracy
      // first); name reads more naturally A-to-Z.
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  }

  const visibleAnalysts = useMemo(() => {
    const filtered = analysts.filter((analyst) => {
      if (statusFilter === 'training') {
        return analyst.requires_qc_review;
      }

      if (statusFilter === 'cleared') {
        return !analyst.requires_qc_review;
      }

      return true;
    });

    const direction = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      if (sortKey === 'name') {
        return a.display_name.localeCompare(b.display_name) * direction;
      }

      if (sortKey === 'pending') {
        return (a.pending_count - b.pending_count) * direction;
      }

      // Accuracy: analysts with no reviewed items yet have nothing to sort by - always push them to
      // the bottom regardless of direction, rather than letting `null` sort arbitrarily first/last.
      if (a.accuracy_rate === null && b.accuracy_rate === null) {
        return 0;
      }

      if (a.accuracy_rate === null) {
        return 1;
      }

      if (b.accuracy_rate === null) {
        return -1;
      }

      return (a.accuracy_rate - b.accuracy_rate) * direction;
    });
  }, [analysts, statusFilter, sortKey, sortDir]);

  // Errors are surfaced inline by QcVerticalsModal itself (it stays open on failure) - left
  // uncaught here so its own catch actually sees them.
  async function handleSaveVerticals(id: string, qcRequiredVerticals: string[] | null) {
    await updateVerticals.mutateAsync({id, verticals: qcRequiredVerticals});
  }

  async function handleApproveAll(email: string, vertical?: string) {
    try {
      const result = await bulkApprove.mutateAsync({
        analyst_email: email,
        ...(vertical ? {policy_violation: vertical} : {})
      });
      const scope = vertical ? ` in ${vertical}` : '';
      showToast(`Approved ${result.approved_count} pending item(s) for ${email}${scope}.`);
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Failed to approve pending items.'), 'error');
    }
  }

  return (
    <>
      <TablePanel>
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHeader
                label="Analyst"
                sortKey="name"
                activeKey={sortKey}
                direction={sortDir}
                onSort={handleSort}
              />
              <SortableHeader
                label="Accuracy"
                sortKey="accuracy"
                activeKey={sortKey}
                direction={sortDir}
                align="right"
                onSort={handleSort}
              />
              <SortableHeader
                label="Pending QC"
                sortKey="pending"
                activeKey={sortKey}
                direction={sortDir}
                align="right"
                onSort={handleSort}
              />
              <TableHead>Content QC</TableHead>
              <TableHead>Actor QC</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT}>
                  <div className="text-muted-foreground flex items-center justify-center gap-2 py-8">
                    <Spinner />
                    Loading…
                  </div>
                </TableCell>
              </TableRow>
            ) : null}
            {!isLoading && visibleAnalysts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="p-0">
                  <Empty className="py-10">
                    <EmptyHeader>
                      <EmptyMedia variant="icon">
                        <Users />
                      </EmptyMedia>
                      <EmptyTitle>No analysts found</EmptyTitle>
                      <EmptyDescription>Try a different search or status filter.</EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                </TableCell>
              </TableRow>
            ) : null}
            {visibleAnalysts.map((analyst) => (
              <TableRow key={analyst.id}>
                <TableCell>
                  <div className="flex items-center gap-2.5">
                    <Avatar size="sm">
                      <AvatarFallback>{initials(analyst.display_name)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="truncate text-xs font-medium">{analyst.display_name}</p>
                      <p className="text-muted-foreground truncate text-xs">{analyst.email}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-right text-xs font-medium tabular-nums">
                  {typeof analyst.accuracy_rate === 'number' ? (
                    <span
                      title={`${analyst.true_positive_count} true positive / ${analyst.false_positive_count} false positive (QC only - an approval with edits counts as false)`}>
                      {formatPercent(analyst.accuracy_rate)}
                    </span>
                  ) : (
                    <span className="text-muted-foreground font-normal">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {analyst.pending_count > 0 ? (
                    <Badge
                      variant="secondary"
                      render={
                        <Link to={`/qc-review?analyst=${encodeURIComponent(analyst.email)}`} />
                      }>
                      {analyst.pending_count}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground text-xs tabular-nums">0</span>
                  )}
                </TableCell>
                <TableCell>
                  <QcTypeGateCell
                    qcRequiredVerticals={analyst.qc_required_verticals}
                    verticals={verticals}
                    type="Content"
                  />
                </TableCell>
                <TableCell>
                  <QcTypeGateCell
                    qcRequiredVerticals={analyst.qc_required_verticals}
                    verticals={verticals}
                    type="Actor"
                  />
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <Button
                      type="button"
                      variant="link"
                      size="xs"
                      className="text-primary-ink"
                      onClick={() => {
                        setEditingAnalyst(analyst);
                      }}>
                      <Pencil />
                      Edit QC
                    </Button>
                    {/* Left out at 0 pending: there is nothing to approve, and a disabled button would
                        give no reason why. */}
                    {analyst.pending_count > 0 ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          disabled={bulkApprove.isPending}
                          render={<Button type="button" variant="secondary" size="sm" />}>
                          {bulkApprove.isPending ? <Spinner /> : null}
                          Approve pending
                          <ChevronDown />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          {verticals.map((vertical) => (
                            <DropdownMenuItem
                              key={vertical}
                              onClick={() => {
                                void handleApproveAll(analyst.email, vertical);
                              }}>
                              {vertical}
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => {
                              void handleApproveAll(analyst.email);
                            }}>
                            Approve all pending (every vertical)
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TablePanel>

      {editingAnalyst ? (
        <QcVerticalsModal
          analyst={editingAnalyst}
          verticals={verticals}
          onSave={async (qcRequiredVerticals) =>
            handleSaveVerticals(editingAnalyst.id, qcRequiredVerticals)
          }
          onClose={() => {
            setEditingAnalyst(null);
          }}
        />
      ) : null}
    </>
  );
}
